import { getLocalVideoTrack } from '../base/tracks';

import logger from './logger';

/**
 * How long to wait after the last quality change before re-acquiring the camera on platforms that cannot narrow an
 * existing capture in place. Dragging the quality slider emits a change per step, and each one costs a camera
 * restart there, so the intermediate steps must not be acted on.
 *
 * @type {number}
 */
const REACQUIRE_DEBOUNCE_MS = 750;

let reacquireTimeout = null;

/**
 * The capture heights `Resolutions` knows about, descending.
 *
 * @type {number[]}
 */
const STANDARD_RESOLUTIONS = [ 2160, 1080, 720, 540, 480, 360, 240, 180 ];

/**
 * Snaps a requested height to the nearest standard capture height at or below it.
 *
 * `RTCUtils.getConstraints` looks the resolution up in the `Resolutions` table by exact key and ignores anything
 * that is not there, falling back to the configured default - so an unsnapped value would quietly reopen the
 * camera at the wrong size rather than failing.
 *
 * @param {number} height - The requested height.
 * @returns {number} A height the Resolutions table recognises.
 */
function _snapToStandardResolution(height) {
    return STANDARD_RESOLUTIONS.find(standard => standard <= height)
        ?? STANDARD_RESOLUTIONS[STANDARD_RESOLUTIONS.length - 1];
}

/**
 * Cancels a re-acquire that has been scheduled but has not run yet.
 *
 * @returns {void}
 */
function _cancelPendingReacquire() {
    if (reacquireTimeout) {
        clearTimeout(reacquireTimeout);
        reacquireTimeout = null;
    }
}

/**
 * Re-acquires the camera at a new resolution and swaps it in.
 *
 * This is the fallback for platforms with no in-place resolution API - react-native-webrtc being the one that
 * matters, where `applyConstraints` is declared but throws `Not implemented`. The swap itself is cheap - it goes
 * through RTCRtpSender.replaceTrack, which does not renegotiate for a same-kind track - but reopening the camera
 * is not: it is visible to the user for roughly half a second. That is why it is debounced, why it refuses to run
 * when the camera is already at the requested height, and why the flag that enables it is opt-in.
 *
 * @param {Object} store - The redux store.
 * @param {number} targetHeight - The height to reopen the camera at.
 * @returns {void}
 */
function _scheduleReacquire(store, targetHeight) {
    _cancelPendingReacquire();

    reacquireTimeout = setTimeout(async () => {
        reacquireTimeout = null;

        const { dispatch, getState } = store;
        const state = getState();
        const localVideo = getLocalVideoTrack(state['features/base/tracks'])?.mhTrack;

        if (!localVideo || localVideo.disposed || localVideo.isMuted()) {
            return;
        }

        // getSettings() is implemented on react-native-webrtc even though applyConstraints is not, so the camera
        // can be asked where it already is. Reopening it to the resolution it is already at would be a visible
        // glitch that buys nothing.
        const current = localVideo.track?.getSettings?.().height;

        // Compared against the snapped height, not the requested one: that is what the camera will actually be
        // reopened at, so comparing with the raw request would reopen it every time the two differ.
        const snapped = _snapToStandardResolution(targetHeight);

        if (Number.isFinite(current) && Math.abs(current - snapped) <= 1) {
            return;
        }

        try {
            // Loaded lazily and by the platform-split path, the same way base/media/middleware.js does it, so the
            // web bundle never pulls in the native implementation or vice versa.
            const { createLocalTracksF } = require('../base/tracks/mhFunctions');
            const { replaceLocalTrack } = require('../base/tracks');

            // `resolution` rather than raw constraints: RTCUtils.getConstraints treats it as a shortcut that
            // *overwrites* constraints.video.height/width, so anything passed the other way is discarded. It is
            // looked up in the Resolutions table by exact key, so a value that is not one of the standard heights
            // silently falls through to the configured default - hence the snap above.
            //
            // cameraDeviceId keeps the exact camera that was in use. Without it getConstraints falls back to
            // facingMode, which would put a participant using the rear camera back on the front one.
            const [ newTrack ] = await createLocalTracksF({
                cameraDeviceId: localVideo.getDeviceId(),
                devices: [ 'video' ],
                resolution: snapped
            }, store);

            if (!newTrack) {
                return;
            }

            // The camera takes time to open. If the user muted, switched device, or changed quality again while
            // it was opening, this track is already stale - drop it rather than putting it on the conference.
            const latest = getLocalVideoTrack(getState()['features/base/tracks'])?.mhTrack;

            if (latest !== localVideo || localVideo.disposed) {
                newTrack.dispose();

                return;
            }

            // On web the camera belongs to conference.js as well, which applies changes one at a time and keeps
            // its own reference to it; replacing behind its back left that reference on the disposed camera and
            // raced its queued changes. `replacing` drops this track if the camera changes while it waits there.
            // Mobile has no conference.js, so the store is the only owner.
            if (typeof APP !== 'undefined' && APP.conference?.useVideoStream) {
                await APP.conference.useVideoStream(newTrack, { replacing: localVideo });
            } else {
                await dispatch(replaceLocalTrack(localVideo, newTrack));
            }
            logger.debug(`Re-acquired the camera at ${targetHeight} to match the selected quality`);
        } catch (error) {
            // The encoding-level cap is still in force, so a failure here costs the local preview and nothing
            // else. Never allowed to reject into the caller, which is a redux listener.
            logger.warn(`Could not re-acquire the camera at ${targetHeight}`, error);
        }
    }, REACQUIRE_DEBOUNCE_MS);
}

/**
 * Makes the camera capture follow the selected quality.
 *
 * Prefers narrowing the existing capture in place, which is instant and invisible. Where the platform has no such
 * API, optionally falls back to reopening the camera - see {@link _scheduleReacquire} for why that is opt-in.
 *
 * Virtual background is handled specially even on platforms where the in-place API is available. The VB effect
 * worker is fed by the *original* `MediaStreamTrack`; constraining that track via `applyConstraints` while the
 * worker is actively drawing from it lands the same constraint on every browser except a narrow set, and the
 * ones that do accept it stay at the new constraint forever - the next user pick (including the user reverting
 * back to HD after a failed narrowing) reaches the same busy hardware. The only path that actually releases the
 * camera long enough for constraints to settle is re-acquisition, which also has the benefit of triggering the
 * `TRACK_ADDED -> toggleBackgroundDevice` middleware that automatically re-applies the effect on the new track.
 *
 * @param {Object} store - The redux store.
 * @param {number} targetHeight - The height the camera should capture at.
 * @returns {Promise<void>} Always resolves; callers are redux listeners and must not be failed by this.
 */
export async function applyLocalCaptureConstraint(store, targetHeight) {
    const state = store.getState();
    const videoQualityConfig = state['features/base/config']?.videoQuality ?? {};
    const virtualBackgroundEnabled
        = state['features/virtual-background']?.backgroundEffectEnabled === true;

    if (videoQualityConfig.applyLocalCaptureConstraint === false) {
        _cancelPendingReacquire();

        return;
    }

    const localVideo = getLocalVideoTrack(state['features/base/tracks'])?.mhTrack;

    if (!localVideo || typeof localVideo.setCaptureResolution !== 'function') {
        // Fall through to re-acquire if the flag is on; we hit this on platforms without `setCaptureResolution`
        // (react-native-webrtc) and when the track exists but the method is unavailable.
        if (videoQualityConfig.reacquireCameraOnQualityChange === true && localVideo) {
            _scheduleReacquire(store, targetHeight);
        }

        return;
    }

    // A quality change supersedes any reopen still queued from the previous one.
    _cancelPendingReacquire();

    // With virtual background active, the in-place narrowing path is structurally broken on most browsers (see the
    // header note). Skip straight to the re-acquire path so the user is never left with a capture that contradicts
    // the quality label. The re-acquire is debounced so a slider drag still does one reopen, not five.
    if (virtualBackgroundEnabled) {
        if (videoQualityConfig.reacquireCameraOnQualityChange === false) {
            logger.warn('VB is active but reacquireCameraOnQualityChange is off - capture will not be resized '
                + 'until either VB is disabled or the flag is enabled; receiver quality cap is still applied.');
        } else {
            _scheduleReacquire(store, targetHeight);
        }

        return;
    }

    let narrowedInPlace = false;

    try {
        narrowedInPlace = await localVideo.setCaptureResolution(targetHeight);
    } catch (error) {
        logger.warn(`Could not narrow the capture to ${targetHeight}`, error);
    }

    if (narrowedInPlace) {
        return;
    }

    // Opt-in, and off unless explicitly switched on: on the platform this exists for, the cost is a camera
    // restart on every quality change.
    if (videoQualityConfig.reacquireCameraOnQualityChange === true) {
        _scheduleReacquire(store, targetHeight);
    }
}
